import { useMutation, useQuery } from '@tanstack/react-query';
import { getPrivateAxiosInstance } from '../../../axiosInstances/axiosPrivate';
import { AcademiaResponse } from '../../../types/common';
import { User, UserProfile } from '../../../types/users';
import { returnQueryConfig } from '../../../utils/commonFunctions';

const useProfileActions = (profileId?: number) => {
  const getUserProfileByIdHandler = useQuery({
    queryKey: ['user-profile-by-id', profileId],
    queryFn: async (): Promise<AcademiaResponse<UserProfile>> => {
      const response = await getPrivateAxiosInstance('user').get(
        `/user/profile/${profileId}/`
      );

      return response.data;
    },

    ...returnQueryConfig(!!profileId),
  });

  // mutation function to create specialized languages entry in database declared here
  const createSpecializedLangsHandler = useMutation({
    mutationFn: async ({
      userId,
      body,
    }: {
      userId: number;
      body: Array<{ code: string; name: string }>;
    }): Promise<AcademiaResponse<User>> => {
      const response = await getPrivateAxiosInstance('user').post(
        `/user/language/create/${userId}/`,
        body
      );

      return response.data;
    },
  });

  // mutation function to update specialized languages declared here
  const updateSpecializedLangsHandler = useMutation({
    mutationFn: async ({
      userId,
      body,
    }: {
      userId: number;
      body: { languages_spoken: Array<number>; updated_by: string };
    }): Promise<AcademiaResponse<User>> => {
      const response = await getPrivateAxiosInstance('user').patch(
        `/user/profile/${userId}/partial/update/`,
        body
      );

      return response.data;
    },
  });

  // mutation handler to create specialized skill entry into the database declared here
  const createSpecializedSkillsHandler = useMutation({
    mutationFn: async ({
      userId,
      body,
    }: {
      userId: number;
      body: Array<{
        skill?: number;
        skill_name?: string;
        skill_level: string;
        created_by: string;
      }>;
    }) => {
      const response = await getPrivateAxiosInstance('user').post(
        `/user/skills/create/${userId}/`,
        body
      );

      return response.data;
    },
  });

  // mutation function to update specialized skills declared here
  const updateSpecializedSkillsHandler = useMutation({
    mutationFn: async ({
      userId,
      body,
    }: {
      userId: number;
      body: Array<{
        skill?: number;
        skill_name?: string;
        skill_level: string;
        updated_by: string;
      }>;
    }): Promise<AcademiaResponse<User>> => {
      const response = await getPrivateAxiosInstance('user').patch(
        `/user/skills/${userId}/partial/update/`,
        body
      );

      return response.data;
    },
  });

  const updateUserProfileHandler = useMutation({
    mutationFn: async ({
      userId,
      body,
    }: {
      userId: number;
      body: FormData;
    }): Promise<AcademiaResponse<User>> => {
      const response = await getPrivateAxiosInstance('user').patch(
        `user/${userId}/partial/update/`,
        body
      );

      return response.data;
    },
  });

  const updateUserCorporateProfileHandler = useMutation({
    mutationFn: async ({
      userId,
      body,
    }: {
      userId: number;
      body: {
        date_of_birth?: string;
        bio?: string;
        linkedin_profile?: string;
        is_first_login?: boolean;
      };
    }): Promise<AcademiaResponse<User>> => {
      const response = await getPrivateAxiosInstance('user').patch(
        `user/profile/${userId}/partial/update/`,
        body
      );

      return response.data;
    },
  });

  return {
    getUserProfileByIdHandler,
    createSpecializedLangsHandler,
    updateSpecializedLangsHandler,
    createSpecializedSkillsHandler,
    updateSpecializedSkillsHandler,
    updateUserProfileHandler,
    updateUserCorporateProfileHandler,
  };
};

export default useProfileActions;
